import { Flex, Box, Button, Divider } from '@opendoor/bricks/core';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';
import { useState } from 'react';
import {
  OdProtosWebData_VirtualAssessmentRecordingInput,
  OdProtosWebData_RecordedFeature as RecordedFeature,
} from '__generated__/globalTypes';
import { VideoUploadButton } from 'sellDirect/components/ssva//VideoUploadButton';
import { VideoUploadCard } from 'sellDirect/components/ssva//VideoUploadCard';
import {
  FileUpload,
  generateS3Key,
  S3UploadDestination,
  uploadFileToS3,
} from 'shared/helpers/s3Helpers';
import { useObservability } from 'shared/helpers/observability';
import { useApolloClient } from '@apollo/client';
import { useQualificationContext } from 'shared/helpers/QualificationContext';
import { Toast, showToast } from '@opendoor/bricks/complex/Toast';
import { TaskCtas } from 'sellDirect/components/ssva/TaskCtas';
import {
  linkForUploadValidity,
  LinkUploadModule,
} from 'sellDirect/components/ssva/LinkUploadModule';
import { useHistory, generatePath } from 'react-router-dom';
import { getHubSSVAUploadRoute, getHubOverviewRoute } from 'shared/helpers/routes';
import { useUpdateVirtualAssessmentRecordings } from 'sellDirect/graphql/useUpdateVirtualAssessmentRecordings';
import { useVirtualAssessmentData } from 'sellDirect/graphql/useVirtualAssessmentData';
import { useSubmitSSVAByRecordedFeature } from 'sellDirect/graphql/useSubmitSSVAByRecordedFeature';
import { SSVAData } from 'sellDirect/OfferContext';
export type VideoInteractionProps = {
  recordedFeature: RecordedFeature;
  uploadedFiles?: OdProtosWebData_VirtualAssessmentRecordingInput[];
  submissionCompleted?: boolean;
  ssvaState: SSVAData['ssvaState'];
};

// TODO: refactor to use FileUploadCard
// component-test-check-ignore
/** Component that contains buttons to upload/take videos, and display video cards */
export const VideoInteraction: React.FC<VideoInteractionProps> = ({
  uploadedFiles,
  recordedFeature,
  submissionCompleted,
  ssvaState,
}) => {
  const { isMobile } = useDevice();
  const apolloClient = useApolloClient();
  const history = useHistory();
  const { sellerInputUUID, addressUUID } = useQualificationContext();
  const { trackEvent, trackError } = useObservability();
  const { updateSSVARecordings, loading: updatingSSVARecordings } =
    useUpdateVirtualAssessmentRecordings();
  const { loading: submittingSSVARecording } = useSubmitSSVAByRecordedFeature();
  const { refetch } = useVirtualAssessmentData();

  const [filesUploading, setFilesUploading] = useState<FileUpload[]>();
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const updateRecordings = (uploads: FileUpload[], feature: RecordedFeature) => {
    const newRecordings: Array<OdProtosWebData_VirtualAssessmentRecordingInput> =
      uploads.map((upload) => {
        return {
          fileName: upload.file.name,
          recordedFeature: feature,
          s3Directory: upload.s3Key,
        };
      }) ?? [];

    trackEvent('self-schedule', 'uploaded-new-recording', { recordedFeature: feature });
    updateSSVARecordings(newRecordings);
  };

  const refetchData = async () => {
    await refetch();
    setLoading(false);
  };

  /** Functions trigger when uploading one individual file */
  const individualOnUpdateFunc = (upload: FileUpload) => {
    setFilesUploading((prevData) => {
      const newData = prevData?.map((prevUpload) =>
        prevUpload.s3Key === upload.s3Key ? upload : prevUpload,
      );
      if (newData) {
        let hasError = false;
        let allCompleted = true;
        const parallelUploads = newData.length;

        for (const fileUpload of newData) {
          if (fileUpload.completed) {
            if (fileUpload.emitMetrics) {
              if (fileUpload.multipartUpload) {
                trackEvent('ssva-multipart-upload-throughput', '', {
                  value: fileUpload.file.size / fileUpload.uploadLatency,
                });
              } else {
                trackEvent('ssva-single-part-upload-throughput', '', {
                  value: fileUpload.file.size / fileUpload.uploadLatency,
                });
              }
              fileUpload.emitMetrics = false;
            }
          } else {
            allCompleted = false;
          }

          if (fileUpload.errorOccurred) {
            hasError = true;
            if (fileUpload.emitMetrics) {
              if (fileUpload.multipartUpload) {
                trackError('ssva-multipart-upload-failed', new Error(fileUpload.errorMessage), {
                  parallelUploads: parallelUploads,
                  size: fileUpload.file.size,
                });
              } else {
                trackError('ssva-single-part-upload-failed', new Error(fileUpload.errorMessage), {
                  parallelUploads: parallelUploads,
                  size: fileUpload.file.size,
                });
              }
              fileUpload.emitMetrics = false;
            }
          }
        }
        if (hasError) {
          setLoading(false);
          setHasError(true);
          return;
        }

        if (allCompleted && !updatingSSVARecordings) {
          updateRecordings(newData, recordedFeature);
          refetchData();
        } else {
          setLoading(true);
        }
      }
      return newData;
    });
  };

  // only allow uploading videos or images
  const isValidFileType = (file: File): boolean => {
    return file.type.startsWith('video') || file.type.startsWith('image');
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.currentTarget.files) {
      let hasInvalidFileType = false;
      const newUploads: FileUpload[] = [];
      Array.from(e.currentTarget.files).forEach((file) => {
        hasInvalidFileType = hasInvalidFileType || !isValidFileType(file);
        // Only upload files with valid file type.
        if (isValidFileType(file)) {
          trackEvent('click', 'ssva-upload', {
            isVideo: file.type.startsWith('video'),
            isImage: file.type.startsWith('image'),
          });
          const fileUpload: FileUpload = {
            file: file,
            s3Key: generateS3Key(file, sellerInputUUID, S3UploadDestination.SSVA),
            progress: 0,
            uploadLatency: 0,
            completed: false,
            errorOccurred: false,
            emitMetrics: false,
            errorMessage: '',
          };
          uploadFileToS3(fileUpload, apolloClient, true, individualOnUpdateFunc);
          newUploads.push(fileUpload);
        }
      });
      if (newUploads.length > 0) {
        setLoading(true);
        setHasError(false);
      }
      setFilesUploading(newUploads);
      if (hasInvalidFileType) {
        showToast(
          'info',
          "We didn't get that. Upload a video instead.",
          'cake-ssva-upload-invalid-file-type-info-toast',
        );
      }
    }
  };

  const filesExist =
    (uploadedFiles && uploadedFiles.length > 0) || (filesUploading && filesUploading.length > 0);

  const linkUploaded =
    uploadedFiles &&
    uploadedFiles.some((file) => linkForUploadValidity(file.s3Directory || '').isValid);

  const noFilesOrUploads = !filesExist && !loading && !updatingSSVARecordings;

  const uploadOrSubmitButtonDisabled =
    loading || updatingSSVARecordings || submissionCompleted || submittingSSVARecording;

  const updatingOrSubmittingSSVA = updatingSSVARecordings || submittingSSVARecording;

  const showAddAnotherVideo = !linkUploaded && !submissionCompleted && filesExist;

  return (
    <>
      <Toast />
      <Flex width="100%" justifyContent="space-between" alignItems="start" flexDirection="column">
        {/* Video Cards */}
        {filesExist && (
          <Flex width="100%" flexDirection="column">
            <Flex mb={5}>
              <Divider />
            </Flex>

            {filesUploading?.map(
              (item, index) =>
                loading && (
                  <VideoUploadCard
                    key={`uploading-${index}`}
                    uploadCompleted={false}
                    fileName={item.file.name}
                    progress={item.progress}
                    hasError={hasError}
                    feature={recordedFeature}
                    mb={3}
                  />
                ),
            )}

            {uploadedFiles?.map((uploadedFile, index) => {
              const fileName = getFileName(uploadedFile);
              return (
                <VideoUploadCard
                  key={`uploaded-${index}`}
                  uploadCompleted={true}
                  fileName={fileName}
                  hasError={hasError}
                  videoLink={uploadedFile.preSignedRecordingUrl || ''}
                  feature={recordedFeature}
                  deleteDisabled={uploadOrSubmitButtonDisabled}
                  mb={3}
                />
              );
            })}
          </Flex>
        )}

        {/* + Add another video link*/}
        {showAddAnotherVideo && (
          <VideoUploadButton
            id={`${recordedFeature}_add_another_video_input`}
            onChange={onFileChange}
            actionText={'Add another video'}
            variant="link"
            loading={updatingOrSubmittingSSVA}
            disabled={uploadOrSubmitButtonDisabled}
            multiple={true}
            withPlusIcon={true}
            recordedFeature={recordedFeature}
          />
        )}
        {!showAddAnotherVideo &&
          submissionCompleted &&
          (ssvaState === 'completed' ? (
            <Button
              variant="link"
              analyticsName="redirect-to-overview-from-ssva-task"
              aria-label="All tasks complete - Redirect to main dashboard"
              onClick={() => history.push(generatePath(getHubOverviewRoute(), { addressUUID }))}
              p={0}
              justifyContent={'start'}
            >
              All tasks complete - Return to overview
            </Button>
          ) : (
            <Button
              variant="link"
              analyticsName="redirect-to-ssva-upload-from-completed-task"
              aria-label="Task Complete - Redirect to self service virtual assessment upload page"
              onClick={() => history.push(generatePath(getHubSSVAUploadRoute(), { addressUUID }))}
              p={0}
              justifyContent={'start'}
            >
              Task complete - Return to upload page
            </Button>
          ))}

        {/* Submit or Save Progress and Close buttons */}
        {filesExist && (
          <TaskCtas
            loading={loading}
            submissionCompleted={submissionCompleted}
            recordedFeature={recordedFeature}
          />
        )}

        {/* Buttons to take/upload videos when there are no uploaded files */}
        {noFilesOrUploads && (
          <Flex
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            flexDirection="column"
          >
            <Box width="100%">
              <VideoUploadButton
                id={`${recordedFeature}_${isMobile ? 'mobile_take' : 'upload'}_video_input`}
                onChange={onFileChange}
                actionText={isMobile ? 'Take Video' : 'Upload Video'}
                variant="primary"
                loading={loading}
                disabled={uploadOrSubmitButtonDisabled}
                multiple={true}
                recordedFeature={recordedFeature}
              />
            </Box>
            <Box width="100%">
              {isMobile && (
                <VideoUploadButton
                  mt={5}
                  id={`${recordedFeature}_mobile_upload_video_input`}
                  onChange={onFileChange}
                  actionText={'Upload Video'}
                  variant="secondary"
                  loading={loading}
                  disabled={uploadOrSubmitButtonDisabled}
                  multiple={true}
                  recordedFeature={recordedFeature}
                />
              )}
            </Box>
          </Flex>
        )}
        {!filesExist && <LinkUploadModule recordedFeature={recordedFeature} />}
      </Flex>
    </>
  );
};

const getFileName = (file: OdProtosWebData_VirtualAssessmentRecordingInput) => {
  if (file?.fileName) {
    return file.fileName;
  }

  const splitS3Directory = file.s3Directory?.split('/') ?? [];
  const inferredFileName = splitS3Directory[splitS3Directory?.length - 1];
  return inferredFileName ?? 'UNKNOWN';
};
